import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import { z } from 'zod'

import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kMatterArtifactVersionsBasePath } from '@/matter-artifacts/constants'
import { MatterArtifactVersionReadSchema, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

const MatterArtifactBaseVersionConflictDetailSchema = z.object({
  code: z.literal('matter_artifact_base_version_conflict'),
  message: z.string(),
  current_matter_artifact_version_id: z.string().uuid().nullable(),
})

type MatterArtifactBaseVersionConflictDetail = z.infer<typeof MatterArtifactBaseVersionConflictDetailSchema>

export class MatterArtifactBaseVersionConflictError extends ApiError {
  readonly currentMatterArtifactVersionId: string | null

  constructor(detail: MatterArtifactBaseVersionConflictDetail) {
    super(409, detail.message, detail)
    this.name = 'MatterArtifactBaseVersionConflictError'
    this.currentMatterArtifactVersionId = detail.current_matter_artifact_version_id
  }
}

export async function postCreateMatterArtifactVersion(baseMatterArtifactVersionId: string, content: string): Promise<MatterArtifactVersionRead> {
  const transactionId = nanoid()
  const token = await getAuth().currentUser?.getIdToken()
  if (!token) throw new Error("Could not retrieve user's authentication token.")

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactVersionsBasePath}`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json; charset=UTF-8',
      'X-Transaction-Id': transactionId,
    },
    body: JSON.stringify({
      base_matter_artifact_version_id: baseMatterArtifactVersionId,
      content,
    }),
  })
  const { data, parseError } = await readResponseJson(response)

  if (!response.ok) {
    const conflictDetail = response.status === 409 ? MatterArtifactBaseVersionConflictDetailSchema.safeParse(getErrorDetail(data)) : null
    if (conflictDetail?.success) throw new MatterArtifactBaseVersionConflictError(conflictDetail.data)

    throw new ApiError(response.status, getErrorMessage(data) || `Failed to create Matter artifact version: ${response.status}`, getErrorDetail(data))
  }

  if (parseError) {
    Sentry.captureException(new Error('Matter artifact version creation response JSON parse failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        baseMatterArtifactVersionId,
        parseError: formatUnknownError(parseError),
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact version creation response JSON')
  }

  const parsed = MatterArtifactVersionReadSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(new Error('Matter artifact version creation response schema validation failed'), {
      extra: {
        ...getResponseDebugContext(response, url),
        baseMatterArtifactVersionId,
        responseKeys: getTopLevelKeys(data),
        issues: parsed.error.issues,
      },
    })
    throw new ApiResponsePayloadError(response.status, 'Invalid Matter artifact version creation response shape', parsed.error.issues)
  }

  return parsed.data
}

function getErrorDetail(data: unknown): unknown {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  return (data as { detail?: unknown }).detail ?? null
}

function getErrorMessage(data: unknown): string | null {
  const detail = getErrorDetail(data)
  if (typeof detail === 'string') return detail
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null
  const message = (detail as { message?: unknown }).message
  return typeof message === 'string' ? message : null
}
